import { Loader2, ShieldCheck } from "lucide-react";
import { Button } from "../ui/button";
import { Skeleton } from "../ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { AdminTableRow } from "./AdminTableRow";
import type { AdminAccount } from "./iam-api";

const HEAD = "px-3 text-xs font-semibold uppercase tracking-[0.22em]";

type AdminTableProps = {
  admins: AdminAccount[];
  myId: number | null;
  actionAdminId: number | null;
  isLoading: boolean;
  hasSearch: boolean;
  onClearSearch: () => void;
  onEditTabs: (admin: AdminAccount) => void;
  onDemote: (admin: AdminAccount) => void;
};

/** The Dashboard IAM directory, in the same frame as the Users Config table. */
export const AdminTable = ({
  admins,
  myId,
  actionAdminId,
  isLoading,
  hasSearch,
  onClearSearch,
  onEditTabs,
  onDemote,
}: AdminTableProps) => {
  return (
    <div className="relative min-h-0 flex-1 overflow-auto">
      <Table className="w-full">
        <TableHeader>
          <TableRow className="sticky top-0 z-10 border-b border-border/60 bg-card/95 backdrop-blur hover:bg-transparent">
            <TableHead className={`sticky left-0 z-20 min-w-[220px] border-r border-border/60 bg-card/95 shadow-[2px_0_4px_-2px_rgba(0,0,0,0.15)] backdrop-blur ${HEAD}`}>
              Admin
            </TableHead>
            <TableHead className={`w-32 ${HEAD}`}>Username</TableHead>
            <TableHead className={HEAD}>Tabs</TableHead>
            <TableHead className={`w-28 ${HEAD}`}>Status</TableHead>
            <TableHead className={`w-32 ${HEAD}`}>Last change</TableHead>
            <TableHead className={`w-20 text-right ${HEAD}`}>Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {admins.length === 0 && isLoading ? (
            Array.from({ length: 6 }).map((_, i) => (
              <TableRow key={i}>
                <TableCell className="px-3 py-3">
                  <div className="flex items-center gap-2">
                    <Skeleton className="size-8 rounded-full" />
                    <div className="space-y-1.5">
                      <Skeleton className="h-3.5 w-28" />
                      <Skeleton className="h-3 w-40" />
                    </div>
                  </div>
                </TableCell>
                <TableCell className="px-3"><Skeleton className="h-3.5 w-24" /></TableCell>
                <TableCell className="px-3">
                  <div className="flex gap-1">
                    <Skeleton className="h-5 w-16 rounded-full" />
                    <Skeleton className="h-5 w-20 rounded-full" />
                    <Skeleton className="h-5 w-14 rounded-full" />
                  </div>
                </TableCell>
                <TableCell className="px-3"><Skeleton className="h-5 w-14 rounded-full" /></TableCell>
                <TableCell className="px-3"><Skeleton className="h-3.5 w-20" /></TableCell>
                <TableCell className="px-3 text-right"><Skeleton className="ml-auto h-7 w-7" /></TableCell>
              </TableRow>
            ))
          ) : admins.length === 0 ? (
            <TableRow>
              <TableCell colSpan={6} className="h-40 text-center">
                <div className="flex flex-col items-center gap-2 text-muted-foreground">
                  <div className="rounded-full bg-muted/40 p-3">
                    <ShieldCheck className="h-10 w-10 opacity-60" />
                  </div>
                  <p>No admin accounts found</p>
                  {hasSearch ? (
                    <Button variant="outline" size="sm" onClick={onClearSearch}>
                      Clear search
                    </Button>
                  ) : (
                    <p className="text-xs">Add an admin, or promote an existing user.</p>
                  )}
                </div>
              </TableCell>
            </TableRow>
          ) : (
            admins.map((admin) => (
              <AdminTableRow
                key={admin.id}
                admin={admin}
                isMe={admin.id === myId}
                isActionLoading={actionAdminId === admin.id}
                onEditTabs={onEditTabs}
                onDemote={onDemote}
              />
            ))
          )}
        </TableBody>
      </Table>
      {isLoading && admins.length > 0 ? (
        <div className="absolute bottom-4 left-4 flex items-center gap-2 rounded-md border border-border/60 bg-background/90 px-3 py-2 text-sm">
          <Loader2 className="size-4 animate-spin text-muted-foreground" />
          <span className="text-muted-foreground">Refreshing...</span>
        </div>
      ) : null}
    </div>
  );
};
